import React, { useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { Tournament, TournamentListSource } from '@/types';
import { useTournamentSearchScope } from '@/hooks/useTournamentSearchScope';
import { useTournamentSearchResults } from '@/hooks/useTournamentSearchResults';
import { useOverlayBox } from '@/hooks/useOverlayBox';
import { LoadMoreSentinel } from '../LoadMoreSentinel';
import { SuggestionList, SuggestionRow } from '../SuggestionRow';
import {
  SuggestionsCaption,
  SuggestionsEmpty,
  SuggestionsError,
  SuggestionsLoading,
  SuggestionsShowAll,
} from '../SuggestionsState';
import styles from './styles.module.scss';

const SKELETON_ROWS = 5;
/** Finger travel that makes a touch a list drag, not a tap. */
const DRAG_THRESHOLD = 10;
/** Room between the last row and the BottomNav / keyboard / panel bottom. */
const END_GAP = 8;

/** Tab name in the caption (the tab page titles). */
const SCOPE_LABEL_KEY: Record<TournamentListSource, string> = {
  my: 'myTournaments',
  public: 'publicTournaments',
  archived: 'archivedTournaments',
};

export interface OverlayPanelProps {
  /** The header — the panel draws below it and within its width. */
  anchorRef: RefObject<HTMLElement>;
  /** The panel element (taps inside it do not leave search mode). */
  panelRef: RefObject<HTMLDivElement>;
  /** Live input text. */
  draft: string;
  /** ↖ — replace the input text, keeping the field focused. */
  fill: (value: string) => void;
  /** Same as Enter ("Show all results"). */
  submit: () => void;
  /** Row tap: open the tournament, keeping the typed query in URL `q`. */
  onSelect: (tournament: Tournament) => void;
  /** A finger started dragging the list (hide the keyboard). */
  onDrag: () => void;
}

/**
 * Results under the header down to the layout-viewport bottom (`document.body`
 * portal, BottomNav draws on top), so the keyboard sliding away never uncovers
 * the page; a trailing spacer ends the content `END_GAP` above the keyboard or
 * BottomNav, whichever covers more. For an empty field the current tab's list
 * with its filters ("<Tab> · N", more pages load on scroll), for typed text its
 * matches; skeleton / error / "Nothing found" instead when there are no rows.
 * Presses inside never take the focus from the field, scrollbar and spacer
 * included.
 */
export const OverlayPanel: React.FC<OverlayPanelProps> = ({
  anchorRef, panelRef, draft, fill, submit, onSelect, onDrag,
}) => {
  const { t } = useTranslation('tournament');
  const box = useOverlayBox(anchorRef);
  const scope = useTournamentSearchScope();
  const results = useTournamentSearchResults(scope, draft);
  const touchStartYRef = useRef<number | null>(null);
  const query = draft.trim();

  // New text → new rows: show them from the top (the tab list may have been
  // scrolled). Keyboard / inset changes keep the scroll position.
  const shownQueryRef = useRef(query);
  useLayoutEffect(() => {
    if (shownQueryRef.current === query) return;
    shownQueryRef.current = query;
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [query, panelRef]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const startY = touchStartYRef.current;
    const y = e.touches[0]?.clientY;
    if (startY === null || y === undefined || Math.abs(y - startY) < DRAG_THRESHOLD) return;
    touchStartYRef.current = null;
    onDrag();
  };

  const renderBody = () => {
    const { view, hasQuery, items } = results;
    if (view === 'loading') return <SuggestionsLoading rows={SKELETON_ROWS} />;
    if (view === 'error') return <SuggestionsError />;
    if (view === 'empty') return <SuggestionsEmpty onShowAll={hasQuery ? submit : undefined} />;
    return (
      <>
        {!hasQuery && results.tabTotal !== undefined && (
          <SuggestionsCaption scope={t(SCOPE_LABEL_KEY[scope.source])} count={results.tabTotal} />
        )}
        <SuggestionList>
          {items.map((tournament) => (
            <SuggestionRow
              key={tournament.id}
              tournament={tournament}
              onSelect={onSelect}
              onFill={hasQuery ? fill : undefined}
            />
          ))}
        </SuggestionList>
        {results.moreTotal !== undefined && (
          <SuggestionsShowAll onClick={submit} count={results.moreTotal} className={styles.showAll} />
        )}
        {results.hasNextPage && (
          <LoadMoreSentinel rootRef={panelRef} armKey={`${items.length}:${results.isFetchingIndex}`} onReach={results.loadMore} />
        )}
      </>
    );
  };

  return createPortal(
    <div
      ref={panelRef}
      className={styles.panel}
      style={{ top: box.top, left: box.left, width: box.width }}
      onMouseDown={(e) => e.preventDefault()}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      data-testid="header-search-panel"
    >
      {renderBody()}
      <div style={{ height: box.bottomInset + END_GAP }} aria-hidden="true" />
    </div>,
    document.body,
  );
};
